import { useState, useEffect, useRef } from "react"
import ReactDOM from "react-dom"
import { useApolloClient } from "@apollo/react-hooks"
import { NextSeo } from "next-seo"
import Router, { useRouter } from "next/router"
import image, { PRODUCT_PHOTOGRAMME } from "lacinetek-common/src/images"

import { useCountry } from "lib/country"
import { useLanguages } from "lib/languages"
import { i18n } from "lib/i18n"
import { FETCH_PRODUCT_SEO_BY_SLUG, FETCH_PRODUCT_SEO } from "lib/graphql/queries"
import { BONUS_ARCHIVES, BONUS_EXCLUSIFS } from "lib/tools/product"
import { getPageCanonicalUrl } from "lib/tools/urls"
import { useProducts, useProductsFilters } from "lib/products"
import isBrowser from "lib/tools/is-browser"
import Gtm from "lib/gtm"
import { fetchOthers } from "lib/film-fetcher"

import { baseProduct } from "components/common/skeleton"
import {
	Header,
	Bundle,
	FilmSheet,
	Others
} from "components/film"
import { Bonuses } from "components/common"
import CustomError from "../../_error"

const Film = ({
	slug,
	ssrFilm,
	ssrSeo,
	ssrOthers,
	autoplay,
	audioDescription
}) => {
	const [id, setId] = useState(null)
	const { getFilms, products } = useProducts()
	const { current } = useLanguages()
	const [currentSlug, setCurrentSlug] = useState(slug)
	const [product, setProduct] = useState(!ssrFilm ? baseProduct : ssrFilm)
	const [seo, setSeo] = useState(ssrSeo)
	const [others, setOthers] = useState(ssrOthers)
	const client = useApolloClient()
	const { country, countrySlug } = useCountry()
	const { availableFilters } = useProductsFilters()

	useEffect(() => {
		if (product.id == 9999999 || slug !== product.linkRewrite) {
			setProduct(baseProduct)
			setOthers(null)
			getFilms([slug], current, country.isoCode)
				.then(data => {
					if (data.length > 0 && data[0]) {
						client.query({query: FETCH_PRODUCT_SEO, variables: { id: data[0].id }, fetchPolicy: 'network-only'})
							.then(({data: { cms: { products: { items: [seo] }}}}) => {
								fetchOthers(data[0].id, country.isoCode)
									.then(oth => {
										setOthers(oth)
										setSeo(seo)
										setProduct(data[0])
										setId(data[0].id)
										setCurrentSlug(data[0].linkRewrite)
									})
						})
					} else {
						setProduct(undefined)
					}
				})
		}
	}, [slug])

	useEffect(() => {
		if (product === undefined || product.id === 9999999) {
			return
		}

		setOthers(null)
		getFilms([product.id], current, country.isoCode)
			.then(data => {
				if (data.length > 0 && data[0]) {
					client.query({query: FETCH_PRODUCT_SEO, variables: { id: data[0].id }, fetchPolicy: 'network-only'})
						.then(({data: { cms: { products: { items: [seo] }}}}) => {
							setSeo(seo)
							setProduct(data[0])
							setId(data[0].id)
							setCurrentSlug(data[0].linkRewrite)
							fetchOthers(data[0].id, country.isoCode)
								.then(oth => {
									setOthers(oth)
								})
							Router.push(Router.pathname, `/${countrySlug}/film/${data[0].linkRewrite}`, { shallow: true })
					})
				} else {
					setProduct(undefined)
				}
			})
	}, [current])

	if (product === undefined) {
		return <CustomError statusCode="404"/>
	}

	if (availableFilters && product) {
		product.genre = "Genre non défini"
		const genres = availableFilters.bottom.find(element => element.title === "genre").values
		Object.keys(product.categories).forEach(function (key){
			const genre = genres.find(el => el.id === product.categories[key])
			if (genre) {
				product.genre = genre.name
			}
		})
	}

	return (
		<Content
			key={`product-${slug}-${i18n.language}-${(product.id == 9999 || !seo) ? "content-loading" : "content"}`}
			product={{...product, ...seo}}
			others={others}
			loading={product.id == 9999999 || !seo}
			autoplay={autoplay}
			audioDescription={audioDescription}
		/>
	)
}

const Content = ({
	product,
	others,
	loading,
	autoplay,
	audioDescription
}) => {
	const { asPath } = useCountry()
	const exclusifsRef = useRef(null)
	const archivesRef = useRef(null)
	const router = useRouter()
	const url = router ? router.asPath : null
	const canonical = getPageCanonicalUrl(router, asPath)
	const [duration, setDuration] = useState(0)

	const goTo = (type) => {
		let ref = null
		switch(type) {
			case BONUS_EXCLUSIFS:
				ref = exclusifsRef
				break
			case BONUS_ARCHIVES:
				ref = archivesRef
				break
		}

		if (ref && ref.current) {
			const block = ReactDOM.findDOMNode(ref.current)
			setTimeout(() => {
				window.scrollTo({
					left: 0,
					top: block.offsetTop - 130,
					behavior: "smooth"
				})
			}, 200)
		}
	}

	let metaTitle = product.metaTitle || product.name
	if (metaTitle.indexOf(" - LaCinetek") !== -1) {
		metaTitle = metaTitle.replace(" - LaCinetek", "")
	}

	return (
		<>
			{ loading
				? null
				: <NextSeo
						title={metaTitle}
						description={product.metaDescription || product.descriptionShort}
						keywords={product.metaKeywords}
						canonical={canonical}
						openGraph={{
							type: "video.movie",
							title: product.metaTitle || product.name,
							description: product.metaDescription || product.descriptionShort,
							url: canonical,
							images: [{url: image(product, PRODUCT_PHOTOGRAMME)}],
							image: {url: image(product, PRODUCT_PHOTOGRAMME)},
							video: {
								duration: product.duration,
								releaseDate: product.year
							}
						}}
					/>
			}
			{ product.id !== 9999999 && product.genre && (
				<Gtm pageType="film" product={product} pagePath={url} pageTitle={metaTitle} />
			) }
			<Header
				film={product}
				loading={loading}
				goTo={goTo}
				autoplay={autoplay === "true"}
				audioDescription={audioDescription === "true"}
				duration={duration}
			/>
			<Bundle film={product} loading={loading} goTo={goTo} />
			<FilmSheet film={product} loading={loading} setDuration={setDuration} />
			<Bonuses product={product} productId={product.id} type={BONUS_EXCLUSIFS} innerRef={exclusifsRef} />
			<Bonuses product={product} productId={product.id} type={BONUS_ARCHIVES} innerRef={archivesRef} />
			{ others
				? <Others product={product} loading={loading} others={others} />
				: null
			}
		</>
	)
}

Film.getInitialProps = async (ctx) => {
	const id = ctx.query.id.split('.')
	const {
		autoplay = false,
		ad = false
	} = ctx.query
	let seo = null
	let ssrOthers = null, ssrFilm = null
	if (!isBrowser) {
		ssrFilm = ctx.res.film
		ssrOthers = ctx.res.filmOthers
		const {data: { cms: { products: { items: [s] }}}} = await ctx.apolloClient.query({query: FETCH_PRODUCT_SEO_BY_SLUG, variables: { query: `linkRewrite.value:"${id}"` }})
		seo = s

		if (!ssrFilm) {
			ctx.res.statusCode = 404
		}
	}

	return {
		slug: id[0],
		ssrSeo: seo,
		ssrFilm,
		ssrOthers,
		autoplay,
		audioDescription: ad
	}
}

export default Film
